import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Loader2, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString()}`;

// booking = { pkg, room, checkIn, checkOut, nights, rooms, travelers }
export const CheckoutDialog = ({ open, onOpenChange, booking }) => {
  const [submitting, setSubmitting] = useState(false);
  const [paymentType, setPaymentType] = useState("full");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pkg = booking?.pkg;
  const room = booking?.room;
  const nights = Math.max(1, booking?.nights || 1);
  const rooms = Math.max(1, booking?.rooms || 1);

  const fullTotal = useMemo(() => {
    if (!pkg) return 0;
    if (room) return room.price_per_night * nights * rooms;
    return pkg.price_from * Math.max(1, booking?.travelers || 1);
  }, [pkg, room, nights, rooms, booking]);

  const depositAmt = (pkg?.deposit || 0) * rooms;
  const payNow = paymentType === "deposit" ? depositAmt : fullTotal;
  const balance = paymentType === "deposit" ? Math.max(0, fullTotal - depositAmt) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        package_id: pkg.id,
        travelers: Math.max(1, Number(booking?.travelers) || 1),
        rooms,
        room_type_id: room?.id || null,
        check_in: booking?.checkIn || null,
        check_out: booking?.checkOut || null,
        nights,
        payment_type: paymentType,
        customer: { name: form.name, email: form.email, phone: form.phone || null },
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  if (!pkg) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="checkout-dialog"
        className="max-w-lg rounded-3xl border-slate-100 bg-white p-0 max-h-[92vh] overflow-y-auto"
      >
        <div className="bg-navy px-8 py-7">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold text-white">
              Confirm your booking
            </DialogTitle>
            <DialogDescription className="text-slate-300">
              {pkg.name}
              {room ? ` · ${room.name}` : ""}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="space-y-4 px-8 py-7">
          {/* Booking summary */}
          <div className="rounded-2xl bg-muted p-4 text-sm" data-testid="checkout-summary">
            <Row label="Dates" value={booking?.checkIn && booking?.checkOut ? `${booking.checkIn} → ${booking.checkOut}` : "Flexible"} />
            <Row label="Nights" value={`${nights}`} />
            <Row label="Rooms" value={`${rooms}`} />
            <Row label="Travelers" value={`${booking?.travelers || 1}`} />
            {room && <Row label="Rate" value={`${money(room.price_per_night)} / night`} />}
          </div>

          {/* Payment type */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-navy">Payment option</Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="checkout-paytype-full"
                onClick={() => setPaymentType("full")}
                className={`rounded-2xl border-2 p-4 text-left transition-colors ${
                  paymentType === "full" ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <span className="block font-display font-bold text-navy">Pay in full</span>
                <span className="mt-1 block text-sm text-slate-500">{money(fullTotal)}</span>
              </button>
              <button
                type="button"
                data-testid="checkout-paytype-deposit"
                onClick={() => setPaymentType("deposit")}
                className={`rounded-2xl border-2 p-4 text-left transition-colors ${
                  paymentType === "deposit" ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <span className="block font-display font-bold text-navy">Deposit</span>
                <span className="mt-1 block text-sm text-slate-500">{money(depositAmt)} now</span>
              </button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-navy">Full name <span className="text-primary">*</span></Label>
              <Input data-testid="checkout-name-input" value={form.name} onChange={update("name")} placeholder="Jordan Rivera" className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-navy">Email <span className="text-primary">*</span></Label>
              <Input data-testid="checkout-email-input" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className="rounded-xl" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-navy">Phone</Label>
            <Input data-testid="checkout-phone-input" value={form.phone} onChange={update("phone")} placeholder="(555) 123-4567" className="rounded-xl" />
          </div>

          {/* Totals */}
          <div className="rounded-2xl bg-muted p-4">
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>Trip total</span>
              <span>{money(fullTotal)}</span>
            </div>
            <div className="mt-2 flex items-center justify-between font-display text-lg font-bold text-navy">
              <span>Due now</span>
              <span data-testid="checkout-due-now">{money(payNow)}</span>
            </div>
            {balance > 0 && (
              <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                <span>Balance due later</span>
                <span data-testid="checkout-balance">{money(balance)}</span>
              </div>
            )}
          </div>

          <Button
            type="submit"
            data-testid="checkout-submit-button"
            disabled={submitting}
            className="w-full rounded-full bg-primary py-6 text-base font-semibold text-primary-foreground btn-glow hover:bg-primary/90"
          >
            {submitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting to secure checkout…</>
            ) : (
              <><CreditCard className="mr-2 h-4 w-4" /> Pay {money(payNow)} securely</>
            )}
          </Button>
          <p className="text-center text-xs text-slate-400">
            Secure payment via Stripe. Test card 4242 4242 4242 4242.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-0.5">
    <span className="text-slate-500">{label}</span>
    <span className="font-medium text-navy">{value}</span>
  </div>
);
